import type { FileRef } from "../types";
import { FileUploadRefusal, type FileUploadService } from "./fileUploads";

/**
 * The file changes still on the wire, owned by the fill SESSION rather than
 * by any one rendering of a question's control.
 *
 * A control is a rendering: a condition can hide it, a section change can
 * unmount it, and it remounts with no memory. The requests it started don't
 * stop for that — the server may already have the bytes — so their tracking
 * must not live in the control either. This registry does: it starts uploads
 * and removals, holds their progress and refusals, counts what's in flight
 * per question (what the fill view waits for before leaving a section or
 * submitting), and applies each result to the answer as the one change it
 * made — this file added, that file removed, never a snapshot of the whole
 * list — through a single updater the host wires to its answer state, so
 * results landing in any order compose instead of overwriting. Other tabs'
 * changes arrive at the next load, which is the reconciliation point.
 */

export interface PendingChange {
  key: number;
  questionId: string;
  kind: "upload" | "remove";
  /** The file's name (an upload) or the attached file's name (a removal). */
  name: string;
  /** The attached file being removed (removals only). */
  fileId?: string;
  /** 0..1 for an upload's bytes; a removal is 1 throughout. */
  fraction: number;
  /** Set once the change was refused; the entry stays until dismissed. */
  error: string | null;
}

export interface UploadRegistry {
  /** Re-render on any change. Returns the unsubscribe. */
  subscribe(listener: () => void): () => void;
  /** A stable snapshot per change — safe for `useSyncExternalStore`. */
  version(): number;
  /** This question's entries, in start order. */
  pendingFor(questionId: string): readonly PendingChange[];
  /** Changes still on the wire for this question (uploads and removals). */
  inFlight(questionId: string): number;
  /** Every question with a change on the wire, visible or not. */
  inFlightQuestions(): string[];
  /** Start uploading; local refusals become entries with an error at once. */
  startUpload(
    questionId: string,
    file: File,
    refuse: (file: File) => string | null,
  ): void;
  /** Cancel an upload still on the wire, or dismiss a refused entry. */
  dismiss(key: number): void;
  /** Detach a file. Resolves to a refusal message, or null when it went. */
  remove(
    questionId: string,
    fileId: string,
    name: string,
  ): Promise<string | null>;
  downloadUrl(fileId: string): Promise<string>;
}

export interface UploadRegistryDeps {
  service: FileUploadService;
  /**
   * Apply one change to a question's attached list, atomically against
   * whatever the list is at that moment. The host wires this to its answer
   * state's functional update.
   */
  applyFiles(
    questionId: string,
    update: (files: readonly FileRef[]) => FileRef[],
  ): void;
}

export function createUploadRegistry(deps: UploadRegistryDeps): UploadRegistry {
  const listeners = new Set<() => void>();
  let entries: PendingChange[] = [];
  const controllers = new Map<number, AbortController>();
  let nextKey = 0;
  let versionNumber = 0;
  // Snapshots per question, rebuilt lazily per version so a subscriber's
  // selector returns the same array while nothing changed.
  let snapshots = new Map<string, readonly PendingChange[]>();

  function changed() {
    versionNumber += 1;
    snapshots = new Map();
    for (const listener of listeners) listener();
  }
  function patch(key: number, update: Partial<PendingChange>) {
    entries = entries.map((entry) =>
      entry.key === key ? { ...entry, ...update } : entry,
    );
    changed();
  }
  function drop(key: number) {
    entries = entries.filter((entry) => entry.key !== key);
    controllers.delete(key);
    changed();
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    version: () => versionNumber,
    pendingFor(questionId) {
      let snapshot = snapshots.get(questionId);
      if (!snapshot) {
        snapshot = entries.filter((entry) => entry.questionId === questionId);
        snapshots.set(questionId, snapshot);
      }
      return snapshot;
    },
    inFlight(questionId) {
      return entries.filter(
        (entry) => entry.questionId === questionId && entry.error === null,
      ).length;
    },
    inFlightQuestions() {
      return [
        ...new Set(
          entries
            .filter((entry) => entry.error === null)
            .map((entry) => entry.questionId),
        ),
      ];
    },

    startUpload(questionId, file, refuse) {
      const key = ++nextKey;
      const refusal = refuse(file);
      entries = [
        ...entries,
        {
          key,
          questionId,
          kind: "upload",
          name: file.name,
          fraction: 0,
          error: refusal,
        },
      ];
      changed();
      if (refusal !== null) return;
      const controller = new AbortController();
      controllers.set(key, controller);
      void deps.service
        .upload({
          questionId,
          file,
          signal: controller.signal,
          onProgress: (fraction) => patch(key, { fraction }),
        })
        .then((attached) => {
          drop(key);
          // Exactly this upload's file, appended to whatever is attached at
          // that moment — never a snapshot, which could put back a file
          // removed meanwhile or hide one another result added.
          deps.applyFiles(questionId, (files) => [
            ...files.filter((f) => f.fileId !== attached.fileId),
            attached,
          ]);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) {
            drop(key);
            return;
          }
          // Settled: nothing left to abort, so Dismiss drops the entry.
          controllers.delete(key);
          patch(key, {
            error:
              error instanceof FileUploadRefusal
                ? error.message
                : "The upload didn't go through. Check your connection and try again.",
          });
        });
    },

    dismiss(key) {
      const controller = controllers.get(key);
      if (controller) controller.abort();
      else drop(key);
    },

    async remove(questionId, fileId, name) {
      const key = ++nextKey;
      entries = [
        ...entries,
        {
          key,
          questionId,
          kind: "remove",
          name,
          fileId,
          fraction: 1,
          error: null,
        },
      ];
      changed();
      try {
        await deps.service.remove({ questionId, fileId });
        // Exactly this removal — never the server's snapshot. Snapshots from
        // overlapping requests can resolve in the wrong order and put a
        // detached file back or drop one a concurrent upload just appended;
        // a delta can only ever take away the one file it names. What other
        // tabs did shows on the next load, which is the reconciliation point.
        deps.applyFiles(questionId, (files) =>
          files.filter((f) => f.fileId !== fileId),
        );
        drop(key);
        return null;
      } catch (error) {
        const message =
          error instanceof FileUploadRefusal
            ? error.message
            : "The file couldn't be removed. Check your connection and try again.";
        patch(key, { error: message });
        return message;
      }
    },

    downloadUrl: (fileId) => deps.service.downloadUrl({ fileId }),
  };
}
